// Rig-mounted archetypes: quadruped chassis (fauna + Sentinel) and cyborg bodies (overhaul 3B/3C).
//
// RIG PRESERVATION LAW (the brief, non-negotiable): every bone's parts are built in JOINT-LOCAL
// space with the origin exactly at the pivot -- thigh spans y in [0,-upper], shin [0,-lower], the
// foot hangs under 0. Nothing here moves, renames or re-parents a joint; builders return part
// lists and the existing bones wear them. boneBounds() below is the fence that proves it.
//
// ONE MESH PER BONE: a bone is a transform boundary, so the weld unit is the bone, not the
// material. All slots weld into one geometry with slot identity baked as VERTEX COLORS under one
// shared material per class -- mesh counts stay at the old box-rig level, which is what keeps the
// draw gate green. Emissives stay separate small meshes (emissive is a material uniform, and the
// Sentinel's lamp colour is driven by its awareness state).

import * as THREE from 'three';
import {
  beveledBox, loftBox, strut, jointCap, cableRun, hydraulicPiston, proceduralHand, faceProfile,
  place, placeParts, creaseNormals, weldSlots, mirrorX,
} from './procedural.js';
import { rngFor } from '../rng.js';

// Slot -> tint for the vertex-color weld. Values, not materials.
// Critic (overhaul 3A round 1): the strider's hull read as "bright white articulated rig...
// the single brightest thing on the vehicle" it happened to stand behind. A night machine is
// DARK with a glowing eye; the bench greys were never re-judged under street light. Hull and
// chrome pulled ~55%.
const TINTS = {
  hull: [0.28, 0.31, 0.36], mech: [0.24, 0.26, 0.30], mechDark: [0.10, 0.11, 0.13],
  chrome: [0.52, 0.55, 0.60], trim: [0.30, 0.32, 0.36], rubber: [0.055, 0.06, 0.065],
  steelDark: [0.16, 0.17, 0.20], cable: [0.09, 0.09, 0.11], liner: [0.12, 0.13, 0.15],
  cloth: [1, 1, 1], skin: [1, 1, 1],  // multiplied by per-instance tint at clone time
  face: [0.72, 0.74, 0.78],           // chrome-silver default; instances may go dark titanium
};

/** Weld a part list into ONE vertex-colored geometry. `tintMap` overrides TINTS per slot. */
export function weldTinted(parts, tintMap = {}) {
  const slots = weldSlots(parts);
  const geos = [];
  for (const [slot, geo] of slots) {
    const t = tintMap[slot] || TINTS[slot] || [1, 0, 1];
    const n = geo.attributes.position.count;
    const col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { col[i * 3] = t[0]; col[i * 3 + 1] = t[1]; col[i * 3 + 2] = t[2]; }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geos.push(geo);
  }
  const merged = geos.length === 1 ? geos[0] : (() => {
    const all = [];
    for (const g of geos) all.push({ geo: g, slot: 'x' });
    const m = weldSlots(all, 32).get('x');
    return m;
  })();
  merged.computeBoundingBox();
  return merged;
}

/** Fence: min/max Y of a part list -- the pivot-preservation check reads this. */
export function boneBounds(parts) {
  let lo = Infinity, hi = -Infinity;
  for (const p of parts) {
    const pos = p.geo.attributes.position;
    for (let i = 1; i < pos.array.length; i += 3) {
      if (pos.array[i] < lo) lo = pos.array[i];
      if (pos.array[i] > hi) hi = pos.array[i];
    }
  }
  return { lo, hi };
}

// ---------------------------------------------------------------------------
// Quadruped chassis (fauna scale=1; the Sentinel passes scale ~3.4)
// ---------------------------------------------------------------------------

/** Body parts in body-group space. `s` scales the whole chassis; sizes mirror the old box rig. */
export function quadBody(key, s = 1, o = {}) {
  const r = rngFor(`chassis.${key}`);
  const P = [];
  const add = (list) => P.push(...list);
  const W = (o.w ?? 0.30) * s, H = (o.h ?? 0.22) * s, L = (o.l ?? 0.72) * s;

  // Exposed skeletal spine: a lofted central beam with cross ribs, NOT a closed box.
  add(placeParts(loftBox([[-L * 0.48, W * 0.14, H * 0.20], [0, W * 0.17, H * 0.26, 0, -H * 0.05],
    [L * 0.48, W * 0.15, H * 0.22]], 0.02 * s, { slot: 'mech' }), { rot: [0, 0, -Math.PI / 2] }));
  for (let i = 0; i < 4; i++) {
    const z = -L * 0.36 + (L * 0.72 / 3) * i;
    add(placeParts(beveledBox(W * 0.9, H * 0.5, 0.045 * s, { bevel: 0.008 * s, slot: 'mechDark' }),
      { pos: [0, -H * 0.05, z] }));
  }
  // Side structural rails.
  for (const side of [-1, 1]) {
    add(strut([side * W * 0.46, H * 0.16, -L * 0.44], [side * W * 0.46, H * 0.16, L * 0.44],
      0.016 * s, 0.016 * s, { slot: 'trim', seg: 7 }));
    add(strut([side * W * 0.46, -H * 0.18, -L * 0.38], [side * W * 0.46, -H * 0.18, L * 0.38],
      0.013 * s, 0.013 * s, { slot: 'trim', seg: 6 }));
  }
  // Top armour cowling -- covers the spine but does NOT fully enclose it (gap at the rear third).
  add(placeParts(beveledBox(W * 1.05, 0.05 * s, L * 0.55, {
    bevel: 0.012 * s, slot: 'hull', panels: { faces: ['pz'], nx: 1, ny: 1, gap: 0.02 * s, depth: 0.012 * s },
  }), { pos: [0, H * 0.32, L * 0.12] }));
  add(placeParts(beveledBox(W * 0.8, 0.04 * s, L * 0.2, { bevel: 0.01 * s, slot: 'hull' }),
    { rot: [0.18, 0, 0], pos: [0, H * 0.26, -L * 0.36] }));
  // Underbelly power unit.
  add(placeParts(beveledBox(W * 0.5, H * 0.34, L * 0.3, { bevel: 0.01 * s, slot: 'steelDark' }),
    { pos: [0, -H * 0.32, -L * 0.05] }));
  // Hip haunch housings, so the legs emerge from something.
  for (const hip of o.hips || []) {
    add(placeParts(beveledBox(0.115 * s, 0.15 * s, 0.19 * s, { bevel: 0.012 * s, slot: 'mechDark' }),
      { pos: [hip[0] * 1.04, hip[1] * s + 0.02 * s, hip[2]] }));
  }
  // Cable bundle from spine into the neck socket.
  add(cableRun([[0, H * 0.30, L * 0.05], [W * 0.12, H * 0.34, L * 0.28], [0, H * 0.30, L * 0.44]],
    { strands: 3, r: 0.008 * s, sag: 0.03 * s, slot: 'cable' }));
  void r;
  return P;
}

/** Aero sensor head in head-group space: wedge, recessed visor liner, antennae. Glow separate. */
export function quadHead(key, s = 1) {
  const P = [];
  // Wedge via loft along +Z (built along Y then rotated): nose narrower and lower.
  P.push(...placeParts(loftBox([[-0.13 * s, 0.095 * s, 0.070 * s], [0.02 * s, 0.10 * s, 0.075 * s],
    [0.13 * s, 0.062 * s, 0.045 * s, 0, -0.012 * s]], 0.015 * s, { slot: 'hull' }),
    { rot: [-Math.PI / 2, 0, 0] }));
  // Visor recess: dark liner slab slightly proud, the emissive slit sits BEHIND its aperture.
  P.push(...placeParts(beveledBox(0.115 * s, 0.035 * s, 0.02 * s, { bevel: 0.005 * s, slot: 'liner' }),
    { pos: [0, 0.008 * s, 0.128 * s] }));
  // Side telemetry antennae.
  for (const side of [-1, 1]) {
    P.push(...strut([side * 0.075 * s, 0.02 * s, -0.02 * s], [side * 0.11 * s, 0.14 * s, -0.06 * s],
      0.006 * s, 0.004 * s, { slot: 'trim', seg: 5 }));
  }
  // Chin sensor pod.
  P.push(...jointCap(0.024 * s, { spherical: true, slot: 'mechDark' }).map((p) => (
    { geo: place(p.geo, { pos: [0, -0.05 * s, 0.10 * s] }), slot: p.slot })));
  return P;
}

/**
 * Limb segments, joint-local. Dual-shaft frames, not solid tubes; hydraulic piston across the
 * knee (bore welds into the THIGH mesh, rod into the SHIN mesh -- the rig drives the extension).
 */
export function quadLimb(upper, lower, s = 1) {
  const shaft = 0.016 * s, gap = 0.030 * s;
  const thigh = [];
  // Hip joint cap at the pivot (origin).
  thigh.push(...jointCap(0.045 * s, { width: 0.085 * s, slot: 'trim' }));
  // Twin shafts + cross braces.
  for (const side of [-1, 1]) {
    thigh.push(...placeParts(loftBox([[0, shaft, shaft * 1.2], [-upper * 0.55, shaft * 1.15, shaft * 1.3, side * 0.004],
      [-upper, shaft * 0.8, shaft], ].map(([y, a, b, xo = 0]) => [y, a, b, xo]), 0.006 * s, { slot: 'mech' }),
      { pos: [side * gap, 0, 0] }));
  }
  for (const t of [0.3, 0.7]) {
    thigh.push(...strut([-gap, -upper * t, 0], [gap, -upper * t, 0], 0.008 * s, 0.008 * s, { slot: 'trim', seg: 5 }));
  }
  // Knee piston bore, angled off the rear of the thigh.
  const pist = hydraulicPiston(upper * 0.62, 0.020 * s, { seg: 8 });
  thigh.push(...placeParts(pist.boreParts.map((p) => ({ geo: p.geo.clone(), slot: p.slot })),
    { rot: [2.55, 0, 0], pos: [0, -upper * 0.18, -shaft * 2.2] }));
  // Knee cap at the bottom of the thigh.
  thigh.push(...jointCap(0.034 * s, { width: 0.07 * s, slot: 'trim' }).map((p) => (
    { geo: place(p.geo, { pos: [0, -upper, 0] }), slot: p.slot })));

  const shin = [];
  for (const side of [-1, 1]) {
    shin.push(...placeParts(loftBox([[0, shaft * 0.85, shaft], [-lower * 0.6, shaft * 0.7, shaft * 0.85],
      [-lower, shaft * 0.5, shaft * 0.6]], 0.005 * s, { slot: 'mechDark' }), { pos: [side * gap * 0.62, 0, 0] }));
  }
  shin.push(...strut([-gap * 0.62, -lower * 0.5, 0], [gap * 0.62, -lower * 0.5, 0], 0.007 * s, 0.007 * s, { slot: 'trim', seg: 5 }));
  // Piston rod, rising from the shin top toward the thigh bore.
  shin.push(...placeParts(pist.rodParts.map((p) => ({ geo: p.geo.clone(), slot: p.slot })),
    { rot: [-2.7, 0, 0], pos: [0, -lower * 0.06, -shaft * 1.6], scale: [1, -1, 1] }));

  const foot = [];
  // Articulated ankle cap + chamfered foot with a RUBBER contact pad under it.
  foot.push(...jointCap(0.026 * s, { width: 0.055 * s, slot: 'trim' }));
  foot.push(...placeParts(beveledBox(0.075 * s, 0.030 * s, 0.115 * s, { bevel: 0.006 * s, slot: 'mech' }),
    { pos: [0, -0.016 * s, 0.018 * s] }));
  foot.push(...placeParts(beveledBox(0.085 * s, 0.014 * s, 0.13 * s, { bevel: 0.005 * s, slot: 'rubber' }),
    { pos: [0, -0.036 * s, 0.02 * s] }));
  return { thigh, shin, foot };
}

// ---------------------------------------------------------------------------
// Cyborg bodies (3C)
// ---------------------------------------------------------------------------

/** Capsule hull with layered plating sheets over it. Axis Y, centred, len = cylinder section. */
function platedCapsule(r, len, slot, plateSlot, nPlates, rr) {
  const P = [{ geo: place(new THREE.CapsuleGeometry(r, len, 3, 10)), slot }];
  for (let i = 0; i < nPlates; i++) {
    const a = rr.range(0, Math.PI * 2);
    const py = rr.range(-len * 0.4, len * 0.4);
    const plate = beveledBox(r * rr.range(0.9, 1.3), len * rr.range(0.34, 0.55), 0.012, { bevel: 0.006, slot: plateSlot });
    placeParts(plate, { pos: [0, 0, r + 0.004], rot: [0, 0, 0] });
    placeParts(plate, { rot: [0, a, 0] });
    placeParts(plate, { pos: [0, py, 0] });
    P.push(...plate);
  }
  return P;
}

/**
 * One cyborg body type (0..5) as bone-keyed part lists. Types differ in build, plating density,
 * clothing (jacket / trench / poncho / none as LOFTED RING BANDS, so the silhouette breaks
 * organically over the limbs), and mech-limb layout.
 */
export function cyborgBody(type, dims) {
  const rr = rngFor(`cyborg.arch.${type}`);
  const { UP, LO } = dims;
  const out = {};

  // --- pelvis + torso (user fix brief: NO monolithic cone -- a multi-panel chest assembly) ----
  // Under-layer first: narrower core capsule, rib rings and spine conduits that the 0.02 m panel
  // gaps deliberately reveal. Then 3-4 curved-set armour plates, bevelled, slightly overlapping.
  const body = [];
  // Flared angular V-pelvis: sharp lofted frame + angled wing plates, widest at the hip line,
  // cutting hard toward the exposed waist above it.
  body.push(...placeParts(loftBox([[-0.045, 0.115, 0.085], [0.065, 0.135, 0.092], [0.145, 0.082, 0.070]],
    0.014, { slot: 'cloth', capA: true, capB: false }), { pos: [0, 0, 0] }));
  for (const side of [-1, 1]) {
    body.push(...placeParts(beveledBox(0.075, 0.11, 0.018, { bevel: 0.007, slot: 'trim' }),
      { rot: [0, side * 0.35, side * 0.42], pos: [side * 0.115, 0.055, 0.02] }));
  }
  {
    // Chest-only under-hull, broadened 1.25x in X; the abdomen stays OPEN so the chrome spine
    // column and the black waist actuators read in silhouette -- the V-taper's whole point.
    const chest = new THREE.CapsuleGeometry(0.115, 0.17, 3, 10);
    chest.scale(1.25, 1, 1.02);
    body.push({ geo: place(chest, { pos: [0, 0.50, 0] }), slot: 'mechDark' });
  }
  for (const ry of [0.50, 0.58]) {
    body.push({ geo: place(new THREE.CylinderGeometry(0.148, 0.150, 0.012, 12), { pos: [0, ry, 0] }), slot: 'cable' });
  }
  body.push(...cableRun([[0.03, 0.20, -0.115], [0, 0.42, -0.135], [-0.02, 0.62, -0.11]],
    { strands: 2 + (type % 2), r: 0.007, sag: 0.012, slot: 'cable', clamps: false }));
  // Segmented mechanical spine at the waist (final brief): four vertebra discs on a core, exposed
  // in the pelvis-torso gap, flanked by dual static hydraulic side-actuators.
  body.push({ geo: place(new THREE.CylinderGeometry(0.030, 0.034, 0.17, 10), { pos: [0, 0.215, -0.015] }), slot: 'chrome' });
  for (let v = 0; v < 4; v++) {
    body.push({ geo: place(new THREE.CylinderGeometry(0.052, 0.052, 0.016, 10), { pos: [0, 0.155 + v * 0.042, -0.03] }), slot: 'trim' });
  }
  for (const side of [-1, 1]) {
    const wp = hydraulicPiston(0.155, 0.016, { extension: 0.5, seg: 7 });
    body.push(...placeParts(wp.parts, { rot: [0.12, 0, side * 0.28], pos: [side * 0.105, 0.135, 0.02] }));
  }
  // Explicit hip socket frames where the leg pivots live (body-group origin height).
  for (const side of [-1, 1]) {
    body.push(...jointCap(0.048, { width: 0.055, slot: 'trim' }).map((p) => (
      { geo: place(p.geo, { pos: [side * 0.09, 0.005, 0] }), slot: p.slot })));
    body.push(...strut([side * 0.06, 0.055, 0], [side * 0.09, 0.012, 0.02], 0.014, 0.012, { slot: 'steelDark', seg: 6 }));
    body.push(...strut([side * 0.06, 0.055, -0.02], [side * 0.09, 0.012, -0.025], 0.012, 0.010, { slot: 'steelDark', seg: 6 }));
  }
  // Armour plates: chest, abdomen, two angled side plates, back plate. Gaps ~0.02 between rows.
  const plateSlot = type % 2 ? 'trim' : 'cloth';
  body.push(...placeParts(beveledBox(0.25, 0.145, 0.022, { bevel: 0.009, slot: plateSlot }),
    { rot: [-0.10, 0, 0], pos: [0, 0.515, 0.118] }));
  body.push(...placeParts(beveledBox(0.21, 0.075, 0.018, { bevel: 0.008, slot: plateSlot }),
    { rot: [-0.22, 0, 0], pos: [0, 0.598, 0.098] }));
  body.push(...placeParts(beveledBox(0.126, 0.075, 0.016, { bevel: 0.007, slot: plateSlot }),
    { rot: [0.10, 0, 0], pos: [0, 0.360, 0.088] }));
  for (const side of [-1, 1]) {
    body.push(...placeParts(beveledBox(0.055, 0.13, 0.016, { bevel: 0.006, slot: plateSlot }),
      { rot: [0, side * 0.55, 0], pos: [side * 0.118, 0.46, 0.045] }));
  }
  body.push(...placeParts(beveledBox(0.19, 0.175, 0.02, { bevel: 0.009, slot: plateSlot }),
    { rot: [0.10, 0, 0], pos: [0, 0.47, -0.108] }));

  // Clothing stays as SMOOTH open ring bands only -- jacket hem (0,3), trench skirt (1,4).
  // The type-2 poncho -- a capped loft tapering to an apex that read as "a giant pyramid" -- is
  // REMOVED per the fix brief; types 2 and 5 now show the plated assembly directly.
  if (type === 0 || type === 3) {
    body.push(...loftBox([[0.16, 0.155, 0.115], [0.30, 0.165, 0.122], [0.44, 0.172, 0.126]],
      0.012, { slot: 'cloth', capA: false, capB: false }));
  } else if (type === 1 || type === 4) {
    body.push(...loftBox([[-0.35, 0.20, 0.155], [-0.05, 0.178, 0.135], [0.25, 0.172, 0.128]],
      0.012, { slot: 'cloth', capA: false, capB: false }));
  }
  // Collar ring + shoulder joint sockets with collar braces (arms no longer float).
  body.push(...loftBox([[0.605, 0.15, 0.10], [0.665, 0.17, 0.108]], 0.010, { slot: 'trim', capA: false, capB: false }));
  for (const side of [-1, 1]) {
    body.push(...jointCap(0.054, { spherical: true, slot: 'trim' }).map((p) => (
      { geo: place(p.geo, { pos: [side * 0.27, 0.615, 0] }), slot: p.slot })));
    body.push(...strut([side * 0.16, 0.645, 0], [side * 0.255, 0.59, 0.03], 0.012, 0.010, { slot: 'steelDark', seg: 6 }));
    const sp = hydraulicPiston(0.13, 0.013, { extension: 0.55, seg: 7 });
    body.push(...placeParts(sp.parts, { rot: [0, 0, side * 1.2], pos: [side * 0.155, 0.545, -0.02] }));
  }
  // Recessed collarbone conduits across the upper chest.
  body.push(...cableRun([[-0.12, 0.60, 0.075], [0, 0.575, 0.09], [0.12, 0.60, 0.075]],
    { strands: 2, r: 0.0045, sag: 0.006, slot: 'cable', clamps: false }));
  // Neck conduit bundle: collar ring up into the skull line.
  body.push(...cableRun([[0.03, 0.615, -0.06], [0.01, 0.66, -0.045], [-0.01, 0.70, -0.02]],
    { strands: 3, r: 0.005, sag: 0.008, slot: 'cable', clamps: false }));
  out.body = body;

  // --- head: tactical helmet, not a bare sphere (user fix brief) ------------------------------
  const head = [];
  // Under-layer: dark mechanical skull the 8 mm plate gaps deliberately reveal.
  head.push({ geo: place(new THREE.CapsuleGeometry(0.070, 0.045, 3, 10), { pos: [0, 0.100, -0.006] }), slot: 'mechDark' });
  // The synthetic face: one anatomical profile, three polished plates (user face brief). The
  // recessed eye socket is IN the silhouette, so the glow lenses sit under the brow, not on it.
  head.push(...faceProfile(1));
  // Ear protectors + side telemetry mounts survive from the helmet round.
  for (const side of [-1, 1]) {
    head.push({ geo: place(new THREE.CylinderGeometry(0.028, 0.030, 0.020, 10),
      { rot: [0, 0, Math.PI / 2], pos: [side * 0.072, 0.098, 0] }), slot: 'trim' });
    head.push(...strut([side * 0.078, 0.112, -0.01], [side * 0.092, 0.155, -0.03], 0.005, 0.004, { slot: 'trim', seg: 5 }));
  }
  // Skull-base cable stub into the collar line.
  head.push(...cableRun([[0.015, 0.045, -0.055], [0.005, 0.005, -0.045], [0, -0.025, -0.020]],
    { strands: 2, r: 0.005, sag: 0.006, slot: 'cable', clamps: false }));
  out.head = head;

  // --- arms: sockets above, servo forearms, ARTICULATED HANDS ---------------------------------
  const mechArm = type === 0 || type === 2 || type === 5;
  const armU = [];
  armU.push(...placeParts(platedCapsule(0.042, 0.16, mechArm ? 'chrome' : 'cloth', 'trim', mechArm ? 2 : 0, rr),
    { pos: [0, -0.15, 0] }));
  // Elbow joint cap at the bottom of the upper arm.
  armU.push(...jointCap(0.034, { width: 0.055, slot: 'trim' }).map((p) => (
    { geo: place(p.geo, { pos: [0, -0.30, 0] }), slot: p.slot })));
  out.armU = armU;
  const armL = [];
  armL.push(...placeParts(platedCapsule(0.036, 0.13, mechArm ? 'chrome' : 'skin', 'trim', 0, rr),
    { pos: [0, -0.115, 0] }));
  if (mechArm) {
    armL.push(...placeParts(beveledBox(0.05, 0.085, 0.05, { bevel: 0.006, slot: 'steelDark' }),
      { pos: [0, -0.115, 0.030] }));
  }
  // The hand, pronated (master brief rest pose): thumbs point +Z FORWARD, finger curl faces
  // the thigh. One chirality of proceduralHand cannot serve both wrists -- the palms must face
  // INWARD on opposite sides -- so the right arm carries a MIRRORED hand.
  const handParts = proceduralHand(1, mechArm ? 'chrome' : 'skin');
  const armR = armL.map((p) => ({ geo: p.geo.clone(), slot: p.slot }));
  armL.push(...placeParts(handParts.map((p) => ({ geo: p.geo.clone(), slot: p.slot })),
    { rot: [0, Math.PI / 2, 0], pos: [0, -0.235, 0] }));
  armR.push(...placeParts(handParts.map((p) => ({ geo: mirrorX(p.geo.clone()), slot: p.slot })),
    { rot: [0, -Math.PI / 2, 0], pos: [0, -0.235, 0] }));
  out.armL = armL;
  out.armR = armR;

  // --- legs, joint-local, origin at pivot -----------------------------------------------------
  const mechLegs = type === 5;
  const thigh = [];
  thigh.push(...placeParts(platedCapsule(0.062, UP * 0.62, mechLegs ? 'mech' : 'cloth', 'trim', mechLegs ? 1 : 0, rr),
    { pos: [0, -UP / 2, 0] }));
  // Dual-layer curved thigh plates: outer guard angled over the capsule, front and side.
  thigh.push(...placeParts(beveledBox(0.085, UP * 0.44, 0.016, { bevel: 0.006, slot: mechLegs ? 'mech' : 'cloth' }),
    { rot: [-0.14, 0, 0], pos: [0, -UP * 0.34, 0.062] }));
  thigh.push(...placeParts(beveledBox(0.055, UP * 0.36, 0.014, { bevel: 0.005, slot: 'trim' }),
    { rot: [-0.10, 0.55, 0], pos: [0.055, -UP * 0.42, 0.03] }));
  // Articulated knee cap at the bottom pivot.
  thigh.push(...jointCap(0.038, { width: 0.055, slot: 'trim' }).map((p) => (
    { geo: place(p.geo, { pos: [0, -UP, 0] }), slot: p.slot })));
  out.thigh = thigh;
  const shin = [];
  shin.push(...placeParts(platedCapsule(0.048, LO * 0.62, mechLegs ? 'mech' : 'cloth', 'trim', 0, rr),
    { pos: [0, -LO / 2, 0] }));
  // Calf servo mount high on the rear of the shin.
  shin.push(...placeParts(beveledBox(0.042, 0.075, 0.045, { bevel: 0.006, slot: 'steelDark' }),
    { pos: [0, -LO * 0.28, -0.045] }));
  if (mechLegs) shin.push(...jointCap(0.045, { width: 0.06, slot: 'trim' }));
  out.shin = shin;
  const foot = [];
  foot.push(...placeParts(beveledBox(0.075, 0.045, 0.21, { bevel: 0.01, slot: 'rubber' }),
    { pos: [0, -0.022, 0.045] }));
  out.foot = foot;
  return out;
}

// ---------------------------------------------------------------------------
// Shared materials + cache
// ---------------------------------------------------------------------------
export const RIG_MATS = {
  mech: null, cyborg: null, face: null,
  get(kind) {
    if (!this[kind]) {
      // 'face' is the chrome-mask finish the face brief specifies: high metal, low rough, so the
      // street neon catches along the cheekbones and nose bridge. It cannot ride vertex colors on
      // the shared class material (roughness is not a vertex attribute), hence its own material.
      this[kind] = kind === 'face'
        ? new THREE.MeshStandardMaterial({
          vertexColors: true, color: 0xffffff, metalness: 0.88, roughness: 0.22, envMapIntensity: 1.8,
        })
        : new THREE.MeshStandardMaterial({
          vertexColors: true, color: 0xffffff,
          roughness: kind === 'mech' ? 0.5 : 0.72,
          metalness: kind === 'mech' ? 0.65 : 0.18,
        });
    }
    return this[kind];
  },
};

const CACHE = new Map();
export function cached(key, build) {
  if (!CACHE.has(key)) CACHE.set(key, build());
  return CACHE.get(key);
}

/** Clone a part list (geometry clones; slots shared). */
export function cloneParts(parts) {
  return parts.map((p) => ({ geo: p.geo.clone(), slot: p.slot }));
}

/** Weld a part list to one tinted mesh and hang it on a bone group. */
export function dressBone(boneGroup, parts, kind, tintMap) {
  // The 'face' slot gets its own polished mesh (+1 non-casting draw per head); everything else
  // stays one mesh per bone.
  const face = parts.filter((p) => p.slot === 'face');
  const rest = parts.filter((p) => p.slot !== 'face');
  let mesh = null;
  if (rest.length) {
    const geo = weldTinted(rest, tintMap);
    creaseNormals(geo, 34);
    mesh = new THREE.Mesh(geo, RIG_MATS.get(kind));
    mesh.castShadow = false;         // creatures are grounded by contact light, not shadow budget
    boneGroup.add(mesh);
  }
  if (face.length) {
    const fgeo = weldTinted(face, tintMap);
    creaseNormals(fgeo, 40);         // bevelSegments-5 contours round out; plate cuts stay sharp
    const fmesh = new THREE.Mesh(fgeo, RIG_MATS.get('face'));
    fmesh.castShadow = false;
    boneGroup.add(fmesh);
  }
  return mesh;
}
